import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Bell, Check, X, Clock, Zap, Bot, Play } from "lucide-react";

const KIND_ICON = { reminder: Clock, automation: Zap, agent: Bot, system: Bell };

export function NotificationCenter({ onRunAgent }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seen = useRef(new Set());
  const first = useRef(true);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
      // toast only for genuinely new notifications (not on first load)
      for (const n of data.notifications || []) {
        if (!seen.current.has(n.id)) {
          seen.current.add(n.id);
          if (!first.current && !n.read) {
            const label = n.kind === "reminder" ? "⏰ Reminder" : n.kind === "automation" ? "⚡ Automation" : "🤖 Stealth";
            toast(`${label}`, { description: n.title });
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              try { new Notification("Stealth Assistance", { body: n.title }); } catch {}
            }
          }
        }
      }
      first.current = false;
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (n) => {
    if (!n.read) { setItems((x) => x.map((i) => (i.id === n.id ? { ...i, read: true } : i))); setUnread((u) => Math.max(0, u - 1)); await api.post(`/notifications/${n.id}/read`).catch(() => {}); }
  };
  const markAll = async () => { setItems((x) => x.map((i) => ({ ...i, read: true }))); setUnread(0); await api.post("/notifications/read-all").catch(() => {}); };
  const remove = async (n) => { setItems((x) => x.filter((i) => i.id !== n.id)); if (!n.read) setUnread((u) => Math.max(0, u - 1)); await api.delete(`/notifications/${n.id}`).catch(() => {}); };

  const runAgent = (n) => { markRead(n); setOpen(false); onRunAgent && onRunAgent(n.meta?.goal || n.title); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Notifications">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium flex items-center justify-center text-[#000000]" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="fixed left-2 right-2 top-[7.75rem] sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[340px] max-h-[70vh] glass-panel !bg-[#0A0A0A]/95 rounded-2xl border border-white/10 z-50 flex flex-col fade-in-up overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="font-heading text-sm font-medium">Notifications</div>
            {items.some((i) => !i.read) && (
              <button data-testid="notification-mark-all" onClick={markAll} className="text-xs text-mono-300 hover:underline flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Mark all read</button>
            )}
          </div>
          <div className="overflow-y-auto thin-scroll">
            {items.length === 0 && <div className="text-sm text-slate-500 text-center py-10">You're all caught up.</div>}
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <div key={n.id} data-testid="notification-item" onClick={() => markRead(n)}
                  className={`px-4 py-3 border-b border-white/5 flex gap-3 cursor-pointer hover:bg-white/5 ${n.read ? "opacity-60" : ""}`}>
                  <Icon className="w-4 h-4 text-mono-300 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-100">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>}
                    <div className="text-[10px] text-slate-600 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    {n.meta?.run_agent && (
                      <button data-testid="notification-run-agent" onClick={(e) => { e.stopPropagation(); runAgent(n); }}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[#000000] rounded-full px-3 py-1 font-medium" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                        <Play className="w-3 h-3" /> Run as agent task
                      </button>
                    )}
                  </div>
                  <button data-testid="notification-delete" onClick={(e) => { e.stopPropagation(); remove(n); }} className="text-slate-600 hover:text-red-400 shrink-0"><X className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
